import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  deriveFormulationSearch,
  isDispensable,
  scriptSureExhausted,
  type FormulationSearchResult,
} from './formulationSearch';
import type { MedicationSearchQueries, PrescribableMedication } from './types';

/**
 * The strength/form lookup behind the medication picker's second step, in two stages.
 *
 * 1. ScriptSure's `$drug-search` drill-down, sending `routedMedId` **and every formulation key on
 *    the drug row**. Sending both in one call is what medplum-provider does, and it is required for
 *    correctness: the vendor's dose-level rows are sometimes incomplete (escitalopram — 5 keys, 4
 *    dose rows) and sometimes absent entirely (Wegovy — 5 keys, 0 dose rows, so a `routedMedId`-only
 *    request returns nothing). Keying off one key returned one strength of five.
 * 2. The RxNorm catalog (`searchFallbackFormulations`), for drugs ScriptSure lists no strengths for
 *    because they have no saved formats (medplum-tracker#17) — `$order-medication` accepts any
 *    current product-level RxNorm code, so those rows are still prescribable. Runs only when stage 1
 *    settles empty.
 *
 * The settled/loading logic lives in `formulationSearch.ts` so its edge cases are unit-testable
 * without react-query.
 */
export function useFormulationSearch(
  drug: PrescribableMedication | null,
  queries: MedicationSearchQueries,
): FormulationSearchResult {
  const routedMedId = drug?.routedMedId;
  const gcnSeqnos = drug?.gcnSeqnos;
  const scriptSureQuery = useQuery({
    // Keys are omitted rather than blanked when there are none: the routers validate them as
    // digits-only, so an empty string is a present-and-invalid value, not an absent one.
    ...queries.searchMedications({
      routedMedId: routedMedId ?? '',
      ...(gcnSeqnos?.length ? { gcnSeqnos } : {}),
    }),
    enabled: Boolean(routedMedId),
  });
  const scriptSureFormulations = useMemo(
    () => ((scriptSureQuery.data ?? []) as PrescribableMedication[]).filter(isDispensable),
    [scriptSureQuery.data],
  );

  const vendorExhausted = scriptSureExhausted(scriptSureQuery, scriptSureFormulations.length);

  const fallbackQuery = useQuery({
    ...queries.searchFallbackFormulations({ drugName: drug?.name ?? '' }),
    enabled: Boolean(drug) && vendorExhausted,
  });
  const fallbackFormulations = useMemo(
    () => ((fallbackQuery.data ?? []) as PrescribableMedication[]).filter(isDispensable),
    [fallbackQuery.data],
  );

  return deriveFormulationSearch({
    scriptSure: {
      isFetching: scriptSureQuery.isFetching,
      isSuccess: scriptSureQuery.isSuccess,
      isError: scriptSureQuery.isError,
    },
    fallback: fallbackQuery,
    scriptSureFormulations,
    fallbackFormulations,
  });
}
